// Timezone-aware date helpers for reports.
//
// Business owners operate in a US time zone, so every report date filter,
// export, and displayed timestamp must be anchored to the *business* calendar
// day — never the browser's own zone and never UTC. Computing a day's
// start/end in the browser (or UTC) produces off-by-one-day windows for anyone
// whose machine isn't set to the business zone, which is the GMT-vs-local bug.
//
// The active "reporting timezone" is resolved by priority:
//   1. an explicit user override (persisted locally),
//   2. the browser's detected zone,
//   3. UTC as a last resort.
// (A configured per-location zone can slot in ahead of the browser later —
// there's no such column stored yet, so the browser is the effective default.)
//
// All math uses native Intl, which is DST-aware for IANA zones — no library.

const OVERRIDE_KEY = 'supernova-report-tz-override';
const FALLBACK_TZ = 'America/Los_Angeles';

/** The app's default timezone — US Pacific. Same value the Reports page has
 *  always fallen back to; displays as "Pacific Time (PDT)" in summer / "(PST)"
 *  in winter. Locations default to this (see products.locations.timezone). */
export const DEFAULT_TIME_ZONE = FALLBACK_TZ;

/** Common US business zones for the override dropdown. IANA ids (DST-safe),
 *  never abbreviations like PST/PDT. */
export const REPORT_TIME_ZONES: { value: string; label: string }[] = [
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'UTC', label: 'UTC' },
];

/** Fuller curated IANA list for per-location timezone configuration (Super
 *  Admin). Superset of REPORT_TIME_ZONES (the US zones the Reports override bar
 *  offers) plus common international zones. Values are IANA ids; labels are
 *  friendly names — `formatTimeZoneLabel` appends the live DST abbreviation. */
export const COMMON_TIME_ZONES: { value: string; label: string }[] = [
  // United States
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  // Americas
  { value: 'America/Toronto', label: 'Toronto' },
  { value: 'America/Mexico_City', label: 'Mexico City' },
  { value: 'America/Sao_Paulo', label: 'São Paulo' },
  // Europe / Africa
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Central European' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg' },
  // Middle East / Asia / Pacific
  { value: 'Asia/Dubai', label: 'Dubai' },
  { value: 'Asia/Kolkata', label: 'India' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Australia/Sydney', label: 'Sydney' },
  { value: 'UTC', label: 'UTC' },
];

/** The browser's detected IANA zone, or the fallback if unavailable. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TZ;
  } catch {
    return FALLBACK_TZ;
  }
}

/** Validate an IANA zone by trying to format with it; invalid names throw. */
export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

// ── Reactive reporting-timezone singleton ────────────────────────────────────
// A tiny external store so the non-hook query layer (withLocalDateRange) can
// read the current zone synchronously, while React components subscribe via
// useSyncExternalStore (see hooks/use-reporting-timezone.ts).

// Resolution priority for the active timezone:
//   1. an explicit user override (reporting timezone bar; persisted locally),
//   2. the active location's configured timezone (set by <TimezoneSync> from the
//      Super Admin per-location setting — the single source of truth),
//   3. US Pacific as the backstop (matches the historical default / column
//      default) when no location is resolved yet.
// The override is intentionally NOT seeded to Pacific: when the user hasn't
// chosen one, the location's zone is the default.
let override: string | null = readStoredOverride();
let locationTimeZone: string | null = null;
const listeners = new Set<() => void>();

function readStoredOverride(): string | null {
  try {
    const v = localStorage.getItem(OVERRIDE_KEY);
    return v && isValidTimeZone(v) ? v : null;
  } catch {
    return null;
  }
}

/** The active timezone (user override → active location → Pacific backstop). */
export function getReportingTimeZone(): string {
  return override || locationTimeZone || FALLBACK_TZ;
}

/** The active location's configured timezone, or null when none resolved yet. */
export function getLocationTimeZone(): string | null {
  return locationTimeZone;
}

/** Set the active location's timezone (called by <TimezoneSync> when the
 *  selected location or its config changes). Notifies subscribers so the picker
 *  and every reader re-resolve. No-op when unchanged or invalid. */
export function setLocationTimeZone(tz: string | null): void {
  const next = tz && isValidTimeZone(tz) ? tz : null;
  if (next === locationTimeZone) return;
  locationTimeZone = next;
  listeners.forEach((l) => l());
}

/** The user's explicit override, or null when following the location zone. */
export function getReportingOverride(): string | null {
  return override;
}

/** Set (or clear, with null) the override and notify subscribers. */
export function setReportingOverride(tz: string | null): void {
  override = tz && isValidTimeZone(tz) ? tz : null;
  try {
    if (override) localStorage.setItem(OVERRIDE_KEY, override);
    else localStorage.removeItem(OVERRIDE_KEY);
  } catch {
    // ignore storage failures (private mode etc.) — in-memory value still holds
  }
  listeners.forEach((l) => l());
}

export function subscribeReportingTimeZone(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** A friendly label with live DST-aware abbreviation, e.g. "Pacific Time (PDT)". */
export function formatTimeZoneLabel(tz: string): string {
  const known = COMMON_TIME_ZONES.find((z) => z.value === tz);
  const base = known?.label ?? tz;
  const abbrev = timeZoneAbbrev(tz);
  return abbrev ? `${base} (${abbrev})` : base;
}

/** DST-aware short name for a zone right now, e.g. "PDT"/"PST"/"EST". */
export function timeZoneAbbrev(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

// ── Zoned date math (generic over any IANA zone) ─────────────────────────────

/** Milliseconds the wall clock in `tz` is offset from UTC at `date`. DST-aware. */
function tzOffsetMs(date: Date, tz: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const map: Record<string, string> = {};
  for (const p of dtf.formatToParts(date)) map[p.type] = p.value;
  const asUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second),
  );
  return asUtc - date.getTime();
}

/** Current calendar date in `tz` as `YYYY-MM-DD` (for `<input type="date">`). */
export function zonedTodayInput(tz: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(),
  );
}

/** Convert a report filter value into the exact UTC instant for `tz`, as an ISO
 *  string. Accepts either a plain `YYYY-MM-DD` (interpreted as the start/end of
 *  that calendar day — 00:00:00.000 / 23:59:59.999) or a `YYYY-MM-DDTHH:mm[:ss]`
 *  from a datetime-local picker (the given wall-clock time in `tz`; when no
 *  seconds are supplied, the end boundary is padded to :59.999 so the selected
 *  minute is inclusive). Pass-through for empty values. */
export function zonedDayBoundaryIso(value: string | undefined, end: boolean, tz: string): string | undefined {
  if (!value) return value;
  const [datePart, timePart] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return value;

  let hh: number;
  let mm: number;
  let ss: number;
  let ms: number;
  if (timePart) {
    const [h, min, s] = timePart.split(':');
    hh = Number(h) || 0;
    mm = Number(min) || 0;
    if (s !== undefined && s !== '') {
      ss = Math.floor(Number(s)) || 0;
      ms = 0;
    } else {
      // Minute-precision picker: make the end boundary inclusive of the minute.
      ss = end ? 59 : 0;
      ms = end ? 999 : 0;
    }
  } else {
    // Date-only → full-day boundary.
    hh = end ? 23 : 0;
    mm = end ? 59 : 0;
    ss = end ? 59 : 0;
    ms = end ? 999 : 0;
  }

  // Treat the desired wall-clock time as if it were UTC, then subtract the
  // zone's offset to land on the true UTC instant.
  const wallAsUtc = Date.UTC(y, m - 1, d, hh, mm, ss, ms);
  const offset = tzOffsetMs(new Date(wallAsUtc), tz);
  return new Date(wallAsUtc - offset).toISOString();
}

// ── Convenience wrappers bound to the *active* reporting zone ─────────────────

/** Today's `YYYY-MM-DD` in the active reporting zone. */
export function reportingTodayInput(): string {
  return zonedTodayInput(getReportingTimeZone());
}

/** Day boundary → UTC instant ISO, in the active reporting zone. */
export function reportingDayBoundaryIso(value: string | undefined, end: boolean): string | undefined {
  return zonedDayBoundaryIso(value, end, getReportingTimeZone());
}
